import { HeatmapMonth } from './heatmapUtils';

// Fixed widths in WalkthroughHeatmapCalendar's grid, in px, used to work out how many months fit a
// width. Keep in sync with the calendar's classes and tracks: w-8 labels, gap-1, the 4px spacer
// before the first month, pr-1 after the grid.
// Card: 28px cells.
export const FIXED_CELL_PX = 28;
// The narrowest spacer between months (the card's stretchy spacers never go below it).
export const MIN_MONTH_SPACER_PX = 4;
const LABEL_COLUMN_PX = 32;
const GAP_PX = 4;
const FIRST_MONTH_SPACER_PX = 4;
const EDGE_ROOM_PX = 4;

// Week tracks per month column when the months wrap after `monthsPerRow`: the most weeks any month
// in that column spans, so months line up vertically across rows.
export const getColumnWeekCounts = (months: HeatmapMonth[], monthsPerRow: number) =>
	Array.from({ length: Math.min(monthsPerRow, months.length) }, (unused, columnIndex) =>
		Math.max(
			...months
				.filter((month, monthIndex) => monthIndex % monthsPerRow === columnIndex)
				.map((month) => month.weeks.length)
		)
	);

// The width of a calendar row with these month columns: the label column, the month spacers, the
// week tracks, the gaps between all the tracks, and the room kept for today's outline.
const getCalendarWidthPx = (columnWeekCounts: number[], cellPx: number, monthSpacerPx: number) => {
	const columnCount = columnWeekCounts.length;
	const weekTrackCount = columnWeekCounts.reduce((total, weekCount) => total + weekCount, 0);
	const trackCount = 1 + columnCount + weekTrackCount;
	return (
		LABEL_COLUMN_PX +
		FIRST_MONTH_SPACER_PX +
		(columnCount - 1) * monthSpacerPx +
		weekTrackCount * cellPx +
		(trackCount - 1) * GAP_PX +
		EDGE_ROOM_PX
	);
};

// How many of the most recent months fit side by side in `widthPx` at the card's cells. Always at
// least one, so the current month shows (scrolling sideways) even in a container too narrow for it.
export const getFittingMonthCount = (months: HeatmapMonth[], widthPx: number) => {
	let fittingCount = 1;
	for (let monthCount = 2; monthCount <= months.length; monthCount++) {
		const recentMonths = months.slice(-monthCount);
		const rowWidth = getCalendarWidthPx(
			getColumnWeekCounts(recentMonths, monthCount),
			FIXED_CELL_PX,
			MIN_MONTH_SPACER_PX
		);
		if (rowWidth > widthPx) {
			break;
		}
		fittingCount = monthCount;
	}
	return fittingCount;
};

// The most months per row, up to `maxMonthsPerRow`, whose rows fit in `widthPx`. Only counts that
// divide the months evenly are tried, so every row is full. Falls back to one month per row.
export const getFittingMonthsPerRow = (
	months: HeatmapMonth[],
	maxMonthsPerRow: number,
	widthPx: number,
	cellPx: number,
	monthSpacerPx: number
) => {
	for (let monthsPerRow = maxMonthsPerRow; monthsPerRow > 1; monthsPerRow--) {
		const fillsEveryRow = months.length % monthsPerRow === 0;
		if (
			fillsEveryRow &&
			getCalendarWidthPx(getColumnWeekCounts(months, monthsPerRow), cellPx, monthSpacerPx) <=
				widthPx
		) {
			return monthsPerRow;
		}
	}
	return 1;
};
